// Explanation:
// https://www.danott.co/posts/react-cache-but-its-a-context
import {
  createContext,
  useReducer,
  useEffect,
  useContext,
  useCallback,
} from "react"
import PropTypes from "prop-types"

const __implementationDetailContext = createContext(null)
const ImplementationDetailProvider = __implementationDetailContext.Provider

const NEW_KEY = "NEW_KEY"
const LOADING = "LOADING"
const SUCCESS = "SUCCESS"
const FAILURE = "FAILURE"
const INCREMENT_REFERENCE_COUNT = "INCREMENT_REFERENCE_COUNT"
const DECREMENT_REFERENCE_COUNT = "DECREMENT_REFERENCE_COUNT"

function reactContextDataFetchingReducer(state, action) {
  switch (action.type) {
    case NEW_KEY:
      return { ...state, [action.key]: [NEW_KEY, null] }
    case LOADING:
      return { ...state, [action.key]: [LOADING, action.value] }
    case SUCCESS:
      return { ...state, [action.key]: [SUCCESS, action.value] }
    case FAILURE:
      return { ...state, [action.key]: [FAILURE, action.value] }
    case INCREMENT_REFERENCE_COUNT:
      return incrementReferenceCount(state, action.key)
    case DECREMENT_REFERENCE_COUNT:
      return decrementReferenceCount(state, action.key)
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}

function buildReferenceCountKey(key) {
  return `__refcount__.${key}`
}

function incrementReferenceCount(state, key) {
  const referenceCountKey = buildReferenceCountKey(key)
  const nextCount = (state[referenceCountKey] || 0) + 1
  return { ...state, [referenceCountKey]: nextCount }
}

function decrementReferenceCount(state, key) {
  const referenceCountKey = buildReferenceCountKey(key)
  const nextCount = state[referenceCountKey] - 1
  const nextState = { ...state, [referenceCountKey]: nextCount }
  if (nextCount === 0) Object.assign(nextState, { [key]: [NEW_KEY, null] })
  return nextState
}

CreateCache.propTypes = {
  children: PropTypes.node.isRequired,
  debug: PropTypes.bool,
}

export function CreateCache({ children, debug = false }) {
  const [state, dispatch] = useReducer(reactContextDataFetchingReducer, {})

  useEffect(() => {
    debug && console.log("CreateCache state", state) // eslint-disable-line no-console
  }, [state, debug])

  return (
    <ImplementationDetailProvider value={[state, dispatch]}>
      {children}
    </ImplementationDetailProvider>
  )
}

let genericResourceIdentifier = 0
function buildGenericCacheKeyGenerator() {
  const identifier = genericResourceIdentifier++
  return (...args) =>
    `genericResourceCacheKey-${identifier}-` + JSON.stringify(...args)
}

export function createResource(
  resolver,
  cacheKeyGenerator = buildGenericCacheKeyGenerator(),
) {
  return (...args) => [() => resolver(...args), cacheKeyGenerator(...args)]
}

export function useCacheRead(resource, ...resourceArgs) {
  const [state, dispatch] = useContext(__implementationDetailContext)
  const [resolver, key] = resource(...resourceArgs)
  let [status, result] = state[key] || [NEW_KEY, null]

  useEffect(() => {
    dispatch({ type: INCREMENT_REFERENCE_COUNT, key })
    return () => {
      dispatch({ type: DECREMENT_REFERENCE_COUNT, key })
    }
  }, [key])

  if (status === NEW_KEY) {
    status = LOADING
    result = resolver()
      .then((value) => dispatch({ type: SUCCESS, key, value }))
      .catch((value) => dispatch({ type: FAILURE, key, value }))
    dispatch({ type: LOADING, key, value: result })
  }

  switch (status) {
    case LOADING:
    case FAILURE:
      throw result
    case SUCCESS:
      return result
    default:
      throw new Error(`Unknown status: ${status}`)
  }
}

// useCacheClear is used to completely remove the Resource from the cache, forcing
// a reload on the next render.
export function useCacheClear(resource, ...resourceArgs) {
  const [_state, dispatch] = useContext(__implementationDetailContext)
  const [_resolver, key] = resource(...resourceArgs)
  return useCallback(() => dispatch({ type: NEW_KEY, key }), [key])
}

// useCacheHydrate is used to load a Resource in the background, and only puts
// it into the cache once it is fully hydrated. This is useful in scenarios where
// we're okay showing the stale data while fetching the new data. An example would
// be responding to PushStream events, and providing a real-time update.
export function useCacheHydrate(resource, ...resourceArgs) {
  const [_state, dispatch] = useContext(__implementationDetailContext)
  const [resolver, key] = resource(...resourceArgs)
  return useCallback(() => {
    resolver()
      .then((value) => dispatch({ type: SUCCESS, key, value }))
      .catch((value) => dispatch({ type: FAILURE, key, value }))
  }, [key])
}
